import { Libraries, useLoadScript } from "@react-google-maps/api";
import { useEffect, useRef } from "react";
import { getGoogleApiKey } from "@/helpers/apiHelpers";
import { FlightTerminalLeg, findProviderTerminalPoi, isAirportPoi, poiIsAtFlightTerminal } from "@/helpers/bookingHelpers";
import { PointsOfInterest } from "@/redux/apis/clientPoi/types";
import { useListProviderPoisQuery } from "@/redux/apis/providerPoi/providerPoiApi";
import { useAppSelector } from "@/redux/hooks";
import { operatorIdSelector } from "@/redux/slices/operator/selectors";
import { findGoogleTerminalPoi } from "../terminalPoi";
import { useAddressSearchCountries } from "./useAddressSearchCountries";

// Module-level so both the loader options and the empty-POI fallback stay referentially stable across
// renders — the effect below has the POI list in its dependencies.
const libraries = ["places"] as Libraries;
const NO_POIS: PointsOfInterest[] = [];

/**
 * TERMINAL SWAP — move a booking location to the terminal the flight actually uses, once the lookup
 * says which. The ARRIVAL terminal for a pickup (DEV-1594); the DEPARTURE terminal for a drop-off
 * (DEV-1487).
 *
 * A client booking an airport stop picks "Sydney Airport" — or last month's terminal — and the terminal
 * is what the driver needs. It is knowable: the same lookup that times the car already carries the
 * leg's `terminal`. So rather than asking the client to know it, the sheet reads it and moves the
 * location, and the Booking Summary discloses the move.
 *
 * ONE hook parameterised by leg rather than two copies. The rules below are subtle — a miss must be
 * remembered but an in-flight lookup must not, provider rows outrank Google, the Google fallback is a
 * billed request — and two implementations of them is how the pickup swap and the drop-off swap end up
 * disagreeing about when a flight "names a terminal".
 *
 * ## What it will not do
 *
 * Nothing runs without a terminal on the leg, and nothing runs when the location already names that
 * terminal at that airport — which is also what makes it idempotent, since the POI it swaps in
 * satisfies exactly that check. Whether the flight is still going ahead is the CALLER's gate: an
 * arrival uses `flightWillArrive`, and a departure asks the same question of its own leg.
 *
 * ## One attempt per location
 *
 * `attempted` holds the flight + terminal the last attempt was made for, cleared whenever the location
 * itself moves. A successful swap needs no guard (the match check closes it), but a MISS does: with no
 * provider row and no Google hit there is nothing to change, so without this the effect would re-ask on
 * every render the flight object is replaced on. Clearing it on a location change is what lets the swap
 * run again after the client edits the address by hand and re-enters the flight.
 */
export const useTerminalSwap = ({
  enabled,
  legResolved,
  flightNumber,
  location,
  leg,
  onSwap,
}: {
  /**
   * The caller's standing gate — view-only, frozen, a lookup error. Deliberately NOT narrowed by
   * whether the flight has resolved yet: it also skips the provider-POI query, and serialising that
   * fetch behind the flight fetch delays every swap by a round trip for no gain. Provider rows are
   * consulted first, so they have to be in hand by the time the flight lands.
   */
  enabled: boolean;
  /** The flight has resolved AND is going ahead — gates the swap itself, never the POI subscription. */
  legResolved: boolean;
  /** The captured flight number, and the reason this location is being moved. */
  flightNumber: string | null;
  /** The POI the swap would replace — the pickup, or the drop-off. */
  location: PointsOfInterest | null;
  /** The resolved leg's airport + terminal. */
  leg: FlightTerminalLeg;
  /** Commit the swap. The caller owns which reducer that is, and what it records as the source. */
  onSwap: (poi: PointsOfInterest) => void;
}) => {
  const operatorId = useAppSelector(operatorIdSelector);
  // The terminal lookup follows the address search's country rule (DEV-1606), so a WLG flight resolves
  // for the same people who can type a Wellington address — on BOTH legs. See `findGoogleTerminalPoi`
  // for why scoping the departure end is safe rather than merely consistent.
  const countries = useAddressSearchCountries();
  // Same argument the address search subscribes with, so this shares that cache entry rather than
  // fetching the provider's POIs a second time.
  const {
    data: providerPois = NO_POIS,
    isLoading: poisLoading,
    isError: poisError,
    isUninitialized: poisUninitialized,
  } = useListProviderPoisQuery(undefined, { skip: !enabled || !operatorId });
  // NOT KNOWN vs known-empty. `isLoading` alone is false for an errored or not-yet-subscribed query and
  // `data` is then undefined, which reads as "the operator has no row for this terminal" and bills the
  // Google fallback over a row that may well exist. With no operator there are no provider POIs to wait
  // for, so that case is settled by construction.
  const providerPoisUnknown = !!operatorId && (poisLoading || poisError || poisUninitialized);
  const { isLoaded: googleLoaded } = useLoadScript({ googleMapsApiKey: getGoogleApiKey(), libraries });

  // The leg is rebuilt on every render by the caller, so the effect keys on its fields rather than its
  // identity — otherwise it re-runs (and re-reads `attempted`) on every render of the sheet.
  const { iata, name, terminal } = leg;

  const attempted = useRef<string | null>(null);
  useEffect(() => {
    attempted.current = null;
  }, [location]);

  // `onSwap` is a fresh closure each render; holding it in a ref keeps it out of the dependency list so
  // a caller that does not memoise cannot make this effect re-run — and re-ask Google — every render.
  const onSwapRef = useRef(onSwap);
  onSwapRef.current = onSwap;

  useEffect(() => {
    // Provider POIs rank ahead of Google, so an attempt made before they land could reach for Google
    // over a row that was about to arrive — and `attempted` would then hold that miss.
    if (!enabled || !legResolved || providerPoisUnknown || !flightNumber) return;
    // An AIRPORT location only. A flight number is not proof of one: `viewDraft` hydrates the stored
    // numbers whatever the addresses are, so opening a booking with a hotel pickup and a flight on it
    // would otherwise move that pickup to a terminal — the client would find it relocated by an edit
    // they came to make somewhere else.
    if (!isAirportPoi(location)) return;
    if (!terminal || !iata) return;
    const target: FlightTerminalLeg = { iata, name, terminal };
    if (poiIsAtFlightTerminal(location, target)) return;

    const key = `${flightNumber}|${iata}|${terminal}`;
    if (attempted.current === key) return;
    attempted.current = key;

    const providerHit = findProviderTerminalPoi(target, providerPois);
    if (providerHit) {
      onSwapRef.current(providerHit);
      return;
    }
    // No provider row for this terminal — fall through to Google, but only once its single loader is
    // ready. Re-asked rather than dropped: this attempt is released so the effect runs again on the
    // render that reports the loader up.
    if (!googleLoaded) {
      attempted.current = null;
      return;
    }
    let cancelled = false;
    let settled = false;
    findGoogleTerminalPoi(target, countries).then((poi) => {
      settled = true;
      if (!cancelled && poi) onSwapRef.current(poi);
    });
    return () => {
      cancelled = true;
      // A lookup cut short by a re-run has not answered yet, so the attempt is released and asked again.
      // Holding the key here loses the swap entirely: the flight query refetches whenever a subscriber
      // mounts onto a stale entry (opening the Date & Time step), which re-runs this effect straight
      // through an in-flight Places call.
      if (!settled) attempted.current = null;
    };
  }, [enabled, legResolved, providerPoisUnknown, flightNumber, location, iata, name, terminal, providerPois, googleLoaded, countries]);
};
